/**
 * Admin — Ranking Diagnostics
 * ─────────────────────────────
 * Transparency page for the Prefecture Ranking pipeline.
 * Shows per-station raw vs adjusted TLH, scoring pipeline
 * (Live Audience vs Stream Analytics), anchor calibration
 * state and 48h learning history.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowLeft, ArrowDown, ArrowUp, ArrowUpDown, BarChart3,
  Brain, CheckCircle2, ChevronDown, ChevronRight, Clock, Cpu, Database,
  Gauge, HelpCircle, Info, Loader2, RefreshCw, Radio, ShieldAlert, Sparkles,
  TrendingUp, Users, Zap
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RTip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getBackendUrl } from '../../utils/api';
import QuickFixActions from './QuickFixActions';

const API = getBackendUrl();

// ── Helpers ──────────────────────────────────────────────────
const fmtTLH = (h) => {
  if (h == null) return '—';
  const n = Number(h);
  if (!isFinite(n)) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k ώρες`;
  if (n < 1) return `${Math.round(n * 60)}λ`;
  return `${n.toFixed(1)}ω`;
};
const fmtPct = (p) => p == null ? '—' : `${Number(p).toFixed(1)}%`;
const fmtNum = (n, d = 1) => n == null ? '—' : Number(n).toFixed(d);
const fmtAgo = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s πριν`;
  if (diff < 3600) return `${Math.round(diff / 60)}λ πριν`;
  if (diff < 86400) return `${Math.round(diff / 3600)}ω πριν`;
  return `${Math.round(diff / 86400)}ημ πριν`;
};

const PERIOD_PRESETS = [
  { key: 'today',         label: 'Σήμερα',            shift: (d) => ({ from: d, to: d }) },
  { key: 'yesterday',     label: 'Χθες',              shift: (d) => {
      const y = new Date(); y.setDate(y.getDate() - 1);
      const s = y.toISOString().slice(0, 10);
      return { from: s, to: s };
  } },
  { key: 'current_week',  label: 'Τρέχουσα εβδομάδα', shift: () => {
      const t = new Date(); const dow = (t.getDay() + 6) % 7;
      const mon = new Date(t); mon.setDate(t.getDate() - dow);
      return { from: mon.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) };
  } },
  { key: 'previous_week', label: 'Προηγούμενη εβδομ.', shift: () => {
      const t = new Date(); const dow = (t.getDay() + 6) % 7;
      const mon = new Date(t); mon.setDate(t.getDate() - dow - 7);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return { from: mon.toISOString().slice(0, 10), to: sun.toISOString().slice(0, 10) };
  } },
  { key: 'last7',         label: 'Τελ. 7 ημέρες',      shift: () => {
      const t = new Date();
      const f = new Date(t); f.setDate(t.getDate() - 6);
      return { from: f.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) };
  } },
];

// ── Mini components ──────────────────────────────────────────
const Stat = ({ label, value, sub, icon: Icon, color = 'blue', tooltip }) => {
  const colors = {
    blue:    'from-blue-500/15 to-blue-700/5 border-blue-500/20 text-blue-300',
    emerald: 'from-emerald-500/15 to-emerald-700/5 border-emerald-500/20 text-emerald-300',
    amber:   'from-amber-500/15 to-amber-700/5 border-amber-500/20 text-amber-300',
    rose:    'from-rose-500/15 to-rose-700/5 border-rose-500/20 text-rose-300',
    purple:  'from-purple-500/15 to-purple-700/5 border-purple-500/20 text-purple-300',
    gray:    'from-gray-600/15 to-gray-800/5 border-gray-500/20 text-gray-300',
  };
  return (
    <div className={`bg-gradient-to-br border rounded-lg p-3 ${colors[color]}`} title={tooltip}>
      <div className="flex items-center gap-1.5 text-[11px] opacity-80 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span>{label}</span>
        {tooltip && <Info className="w-3 h-3 opacity-40" />}
      </div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
};

const TierBadge = ({ tier }) => {
  const cfg = {
    A: { bg: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', label: 'A · Real' },
    B: { bg: 'bg-sky-500/20 text-sky-300 border-sky-500/40', label: 'B · Mixed' },
    C: { bg: 'bg-amber-500/20 text-amber-300 border-amber-500/40', label: 'C · Low' },
    D: { bg: 'bg-rose-500/20 text-rose-300 border-rose-500/40', label: 'D · Auto' },
  }[tier] || { bg: 'bg-gray-600/20 text-gray-400 border-gray-500/40', label: tier || '—' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
};

const ModeBadge = ({ mode }) => {
  if (mode === 'live_audience') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Live Audience
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-[10px] whitespace-nowrap">
      <Sparkles className="w-2.5 h-2.5" />
      Stream Analytics
    </span>
  );
};

// ── Station detail panel (expanded row) ──────────────────────
const StationDetail = ({ s, token, onRefetch, dateFrom, dateTo }) => {
  const isLive = s.data_mode === 'live_audience';
  const deltaColor = s.tlh.delta_pct == null
    ? 'text-gray-400'
    : Math.abs(s.tlh.delta_pct) < 10
      ? 'text-emerald-400'
      : Math.abs(s.tlh.delta_pct) < 50
        ? 'text-amber-400'
        : 'text-rose-400';

  return (
    <div className="bg-gray-900/60 border-t border-white/5 p-4 space-y-4">
      {/* TLH comparison — the core diagnostic */}
      <div>
        <div className="text-[11px] uppercase text-gray-500 font-semibold mb-2 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" />
          Ανάλυση TLH — Raw vs Adjusted
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Raw TLH" value={fmtTLH(s.tlh.raw)} sub="avg × hours πραγματικά" icon={Database} color="gray" />
          <Stat label="Adjusted TLH" value={fmtTLH(s.tlh.adjusted)} sub="τιμή που χρησιμοποιείται στο ranking" icon={Gauge} color="blue" />
          <Stat label="Διαφορά" value={s.tlh.delta == null ? '—' : (s.tlh.delta >= 0 ? '+' : '') + fmtTLH(Math.abs(s.tlh.delta))}
                sub={`${(s.tlh.delta || 0) >= 0 ? 'αυξήθηκε' : 'μειώθηκε'} από calibration`}
                icon={Activity} color={s.tlh.delta >= 0 ? 'emerald' : 'rose'} />
          <Stat label="Μεταβολή %" value={s.tlh.delta_pct == null ? '—' : `${s.tlh.delta_pct > 0 ? '+' : ''}${s.tlh.delta_pct}%`}
                sub={Math.abs(s.tlh.delta_pct || 0) > 50 ? '⚠ μεγάλη απόκλιση' : 'εντός αποδεκτού εύρους'}
                icon={Cpu} color={Math.abs(s.tlh.delta_pct || 0) > 50 ? 'rose' : 'purple'} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Raw listeners (Live Audience) */}
        <div className={`border rounded-lg p-3 ${isLive ? 'border-emerald-500/30 bg-emerald-500/[0.03]' : 'border-gray-600/30 bg-gray-500/[0.03]'}`}>
          <div className="flex items-center gap-2 text-[11px] uppercase text-gray-400 font-semibold mb-2">
            <Radio className="w-3.5 h-3.5" />
            Live Audience δεδομένα
            {!isLive && <span className="text-[10px] text-gray-500 normal-case font-normal">(ανενεργό · χωρίς endpoint)</span>}
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><div className="text-gray-500 text-[10px]">Peak</div><div className="text-emerald-300 tabular-nums font-semibold">{s.raw_listeners.peak || '—'}</div></div>
            <div><div className="text-gray-500 text-[10px]">Average</div><div className="text-blue-300 tabular-nums font-semibold">{fmtNum(s.raw_listeners.avg)}</div></div>
            <div><div className="text-gray-500 text-[10px]">Min</div><div className="text-amber-300 tabular-nums">{s.raw_listeners.min ?? '—'}</div></div>
            <div><div className="text-gray-500 text-[10px]">Samples</div><div className="tabular-nums">{s.raw_listeners.samples.toLocaleString('el-GR')}</div></div>
            <div><div className="text-gray-500 text-[10px]">Hours w/ data</div><div className="tabular-nums">{s.raw_listeners.hours_with_data}</div></div>
            <div><div className="text-gray-500 text-[10px]">Coverage</div><div className={`tabular-nums ${s.raw_listeners.coverage_pct < 50 ? 'text-amber-300' : 'text-emerald-300'}`}>{fmtPct(s.raw_listeners.coverage_pct)}</div></div>
          </div>
          <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-gray-500 flex flex-wrap gap-x-3">
            <span>Source: <span className="text-gray-300">{s.raw_listeners.source_type || '—'}</span></span>
            <span>Smoothed avg: <span className="text-gray-300">{fmtNum(s.raw_listeners.smoothed_avg)}</span></span>
          </div>
        </div>

        {/* Plays (Stream Analytics) */}
        <div className={`border rounded-lg p-3 ${!isLive ? 'border-indigo-500/30 bg-indigo-500/[0.03]' : 'border-gray-600/30 bg-gray-500/[0.03]'}`}>
          <div className="flex items-center gap-2 text-[11px] uppercase text-gray-400 font-semibold mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            Stream Analytics δεδομένα
            {isLive && <span className="text-[10px] text-gray-500 normal-case font-normal">(δευτερεύον · έχει real listeners)</span>}
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><div className="text-gray-500 text-[10px]">Total plays</div><div className="tabular-nums font-semibold">{(s.plays.total || 0).toLocaleString('el-GR')}</div></div>
            <div><div className="text-gray-500 text-[10px]">Plays/hr</div><div className="tabular-nums text-indigo-300 font-semibold">{fmtNum(s.plays.per_hour, 2)}</div></div>
            <div><div className="text-gray-500 text-[10px]">Unique titles</div><div className="tabular-nums">{s.plays.unique_titles}</div></div>
            <div><div className="text-gray-500 text-[10px]">Unique/hr</div><div className="tabular-nums">{fmtNum(s.plays.unique_per_hour, 2)}</div></div>
            <div><div className="text-gray-500 text-[10px]">Quality</div><div className="tabular-nums">{fmtNum(s.scoring.quality_score, 3)}</div></div>
            <div><div className="text-gray-500 text-[10px]">Quality applied</div><div>{s.scoring.quality_applied ? '✓ Ναι' : '—'}</div></div>
          </div>
          {s.scoring.estimated_listeners != null && (
            <div className="mt-2 pt-2 border-t border-indigo-500/20 text-[10px]">
              <span className="text-indigo-300">Estimated listeners:</span>{' '}
              <span className="font-bold">{fmtNum(s.scoring.estimated_listeners)}</span>
              <span className="text-gray-500"> (από calibration ratio)</span>
            </div>
          )}
        </div>
      </div>

      {/* Scoring pipeline */}
      <div className="border border-purple-500/20 bg-purple-500/[0.03] rounded-lg p-3">
        <div className="flex items-center gap-2 text-[11px] uppercase text-gray-400 font-semibold mb-2">
          <Brain className="w-3.5 h-3.5" />
          Scoring Pipeline
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-xs tabular-nums">
          <div><div className="text-gray-500 text-[10px]">Base audience</div><div>{fmtNum(s.scoring.base_audience, 1)}</div></div>
          <div><div className="text-gray-500 text-[10px]">Tier</div><TierBadge tier={s.scoring.tier} /></div>
          <div><div className="text-gray-500 text-[10px]">Confidence</div><div className="text-purple-300">{fmtNum(s.scoring.confidence, 2)}</div></div>
          <div><div className="text-gray-500 text-[10px]">Anchor conf.</div><div>{fmtNum(s.scoring.anchor_confidence, 2)}</div></div>
          <div><div className="text-gray-500 text-[10px]">Variety</div><div>{fmtNum(s.scoring.variety_score, 3)}</div></div>
          <div><div className="text-gray-500 text-[10px]">Consistency</div><div>{fmtNum(s.scoring.consistency_score, 3)}</div></div>
          <div><div className="text-gray-500 text-[10px]">Final score</div><div className="text-purple-300 font-semibold">{fmtNum(s.scoring.final_score, 1)}</div></div>
          <div><div className="text-gray-500 text-[10px]">Normalized</div><div>{fmtNum(s.scoring.normalized_score, 3)}</div></div>
        </div>
      </div>

      {/* Endpoint + SPI + Flags */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div className="border border-white/10 rounded-lg p-3">
          <div className="text-[11px] uppercase text-gray-400 font-semibold mb-1.5">Endpoint</div>
          <div className="space-y-1">
            <div>stats_url: <span className={s.endpoint.has_stats_url ? 'text-emerald-400' : 'text-gray-500'}>{s.endpoint.has_stats_url ? '✓' : '—'}</span></div>
            <div>listeners_api_url: <span className={s.endpoint.has_listeners_api_url ? 'text-emerald-400' : 'text-gray-500'}>{s.endpoint.has_listeners_api_url ? '✓' : '—'}</span></div>
            <div>Last success: <span className={s.endpoint.stale ? 'text-rose-400' : 'text-gray-300'}>{fmtAgo(s.endpoint.last_success)}</span></div>
          </div>
        </div>
        <div className="border border-white/10 rounded-lg p-3">
          <div className="text-[11px] uppercase text-gray-400 font-semibold mb-1.5">Stream Health (SPI)</div>
          {s.spi ? (
            <div className="space-y-1">
              <div>SPI: <span className="tabular-nums">{fmtNum(s.spi.spi, 0)}</span></div>
              <div>Label: <span className="text-gray-300">{s.spi.label || '—'}</span></div>
              <div>Reliability: <span className="text-gray-300">{s.spi.reliability || '—'}</span></div>
            </div>
          ) : <div className="text-gray-500">Χωρίς SPI data</div>}
        </div>
        <div className="border border-white/10 rounded-lg p-3">
          <div className="text-[11px] uppercase text-gray-400 font-semibold mb-1.5">Flags</div>
          <div className="flex flex-wrap gap-1">
            {s.flags.has_real_listeners && <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded text-[10px]">has_real</span>}
            {s.flags.via_activity_fallback && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded text-[10px]">activity_fallback</span>}
            {s.flags.estimated && <span className="px-1.5 py-0.5 bg-indigo-500/20 text-indigo-300 rounded text-[10px]">estimated</span>}
            {s.flags.listener_data_missing && <span className="px-1.5 py-0.5 bg-rose-500/20 text-rose-300 rounded text-[10px]">listener_data_missing</span>}
            {!Object.values(s.flags).some(Boolean) && <span className="text-gray-500 text-[10px]">—</span>}
          </div>
        </div>
      </div>

      {/* Warnings */}
      {s.warnings.length > 0 && (
        <div className="border border-amber-500/30 bg-amber-500/[0.03] rounded-lg p-3">
          <div className="flex items-center gap-2 text-[11px] uppercase text-amber-300 font-semibold mb-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            Προειδοποιήσεις ({s.warnings.length}) — Quick Fix actions διαθέσιμα
          </div>
          <ul className="space-y-2 text-xs text-amber-200">
            {s.warnings.map((w, i) => (
              <li key={i} className="flex flex-col gap-1 border-b border-amber-500/10 last:border-0 pb-2 last:pb-0">
                <div className="flex gap-2">
                  <span>•</span>
                  <span>{w}</span>
                </div>
                <div className="ml-4">
                  <QuickFixActions
                    station={s}
                    warning={w}
                    token={token}
                    onRefetch={onRefetch}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────
const RankingDiagnostics = () => {
  const navigate = useNavigate();
  const [prefectures, setPrefectures] = useState([]);
  const [selectedPref, setSelectedPref] = useState('');
  const [periodKey, setPeriodKey] = useState('current_week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [sort, setSort] = useState({ key: 'rank', dir: 'asc' });
  const token = localStorage.getItem('token');

  const period = useMemo(() => {
    const p = PERIOD_PRESETS.find(x => x.key === periodKey) || PERIOD_PRESETS[2];
    return p.shift();
  }, [periodKey]);

  // Fetch prefecture list
  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const me = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        const u = await me.json();
        const uid = u.user_id || u.id;
        const r = await fetch(`${API}/api/songs-analyzer/prefecture-options?user_id=${uid}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setPrefectures(d.options || []);
          if (d.options?.[0]) setSelectedPref(d.options[0].value);
        }
      } catch (e) { console.error(e); }
    };
    if (token) fetchPrefs();
  }, [token]);

  // Fetch diagnostics
  const fetchData = useCallback(async () => {
    if (!selectedPref || !token) return;
    setLoading(true);
    try {
      const url = `${API}/api/diagnostic/ranking-diagnostics?prefecture=${selectedPref}&date_from=${period.from}&date_to=${period.to}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        setData(await r.json());
      } else {
        setData(null);
      }
    } catch (e) {
      console.error(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedPref, period.from, period.to, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const sortedStations = useMemo(() => {
    if (!data?.stations) return [];
    const arr = [...data.stations];
    const { key, dir } = sort;
    const m = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va, vb;
      if (key === 'rank') { va = a.rank; vb = b.rank; }
      else if (key === 'name') return a.station_name.localeCompare(b.station_name, 'el-GR') * m;
      else if (key === 'market_share') { va = a.market_share; vb = b.market_share; }
      else if (key === 'tlh_raw') { va = a.tlh.raw; vb = b.tlh.raw; }
      else if (key === 'tlh_adj') { va = a.tlh.adjusted; vb = b.tlh.adjusted; }
      else if (key === 'tlh_delta') { va = a.tlh.delta_pct ?? -9999; vb = b.tlh.delta_pct ?? -9999; }
      else if (key === 'peak') { va = a.raw_listeners.peak; vb = b.raw_listeners.peak; }
      else if (key === 'tier') return (a.scoring.tier || 'Z').localeCompare(b.scoring.tier || 'Z') * m;
      else if (key === 'warnings') { va = a.warnings.length; vb = b.warnings.length; }
      return ((va ?? 0) - (vb ?? 0)) * m;
    });
    return arr;
  }, [data, sort]);

  const handleSort = (key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: ['rank', 'name', 'tier'].includes(key) ? 'asc' : 'desc' });
  };

  const Th = ({ k, label, align = 'left', title }) => {
    const active = sort.key === k;
    const Ic = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th className={`px-2 py-2 text-${align} text-[11px] font-semibold text-gray-400`} title={title}>
        <button onClick={() => handleSort(k)} className={`inline-flex items-center gap-1 px-1 rounded ${active ? 'text-purple-300' : 'hover:text-gray-200'}`}>
          {label} <Ic className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  const ls = data?.learning_stats || {};
  const ag = ls.anchor_global || {};
  const ag_status = ag.status || '—';
  const driftChart = (ls.drift_history || [])
    .filter(d => d.scope === 'global')
    .map(d => ({
      t: new Date(d.ts).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' }),
      ratio: d.ratio,
      count: d.anchor_count,
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 to-purple-950/30 text-gray-200">
      {/* Header */}
      <div className="border-b border-white/5 bg-black/40 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white p-1.5 rounded hover:bg-white/5"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Brain className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <div className="font-semibold">Ranking Diagnostics</div>
                <div className="text-[11px] text-gray-500">Διαφάνεια υπολογισμού TLH · anchor calibration · learning progress</div>
              </div>
            </div>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded disabled:opacity-40"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4 space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 bg-white/[0.02] rounded-lg p-3 border border-white/5">
          <span className="text-[11px] text-gray-500 mr-1">Νομός:</span>
          <select
            data-testid="prefecture-select"
            className="bg-gray-900 border border-white/10 rounded px-2 py-1 text-xs text-gray-200"
            value={selectedPref}
            onChange={(e) => setSelectedPref(e.target.value)}
          >
            {prefectures.map(p => (
              <option key={p.value} value={p.value}>{p.label} ({p.stations_count})</option>
            ))}
          </select>
          <span className="text-[11px] text-gray-500 ml-3 mr-1">Περίοδος:</span>
          <div className="flex gap-1 flex-wrap">
            {PERIOD_PRESETS.map(p => (
              <button
                key={p.key}
                data-testid={`period-${p.key}`}
                onClick={() => setPeriodKey(p.key)}
                className={`px-2 py-1 text-[11px] rounded-full border whitespace-nowrap ${
                  periodKey === p.key
                    ? 'bg-blue-500/20 text-blue-200 border-blue-500/50'
                    : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <span className="text-[10px] text-gray-500 ml-auto">{period.from} → {period.to}</span>
        </div>

        {loading && (
          <div className="py-16 flex items-center justify-center text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Φόρτωση diagnostics...
          </div>
        )}

        {!loading && data && (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <Stat label="Σταθμοί" value={data.totals.total_stations} icon={Radio} color="blue" />
              <Stat label="Live Audience" value={data.totals.live_audience_count} sub="με endpoint" icon={Activity} color="emerald" />
              <Stat label="Stream Analytics" value={data.totals.stream_analytics_count} sub="estimated" icon={Sparkles} color="purple" />
              <Stat label="Raw TLH σύνολο" value={fmtTLH(data.totals.total_raw_tlh)} icon={Database} color="gray" />
              <Stat label="Adjusted TLH" value={fmtTLH(data.totals.total_adjusted_tlh)} icon={Gauge} color="blue" />
              <Stat label="Delta από calibration"
                    value={data.totals.tlh_delta_pct == null ? '—' : `${data.totals.tlh_delta_pct > 0 ? '+' : ''}${data.totals.tlh_delta_pct}%`}
                    sub={fmtTLH(Math.abs(data.totals.tlh_delta))}
                    icon={Cpu}
                    color={Math.abs(data.totals.tlh_delta_pct || 0) > 30 ? 'amber' : 'emerald'} />
            </div>

            {/* Tier distribution */}
            <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3">
              <div className="text-[11px] uppercase text-gray-400 font-semibold mb-2 flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5" /> Κατανομή Tiers
              </div>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(data.totals.tier_distribution).map(([t, c]) => (
                  <div key={t} className="flex items-center gap-2">
                    <TierBadge tier={t} />
                    <span className="text-gray-300 font-semibold">{c}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Learning progress */}
            <div className="bg-gradient-to-br from-purple-950/30 to-blue-950/20 border border-purple-500/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-purple-400" />
                  <div>
                    <div className="font-semibold text-purple-200">Anchor Calibration · Learning Progress</div>
                    <div className="text-[11px] text-gray-500">Πως το σύστημα «μαθαίνει» από σταθμούς με πραγματικούς ακροατές για να εκτιμήσει αυτούς χωρίς endpoint</div>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-[10px] font-mono ${
                  ag_status === 'ok' ? 'bg-emerald-500/20 text-emerald-300'
                  : ag_status === 'insufficient_anchors' ? 'bg-amber-500/20 text-amber-300'
                  : 'bg-gray-500/20 text-gray-400'
                }`}>status: {ag_status}</span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-3">
                <Stat label="Effective ratio" value={fmtNum(ls.effective_ratio, 4)} sub="plays/listener" icon={Zap} color="purple" tooltip="Τελικός ratio που χρησιμοποιείται για estimation" />
                <Stat label="Global anchors" value={ag.anchor_count ?? '—'} sub={`εκ των ${ag.min_required || 5} απαιτούμενων`} icon={Users} color="blue" />
                <Stat label="Trimmed ratio" value={fmtNum(ag.median_trimmed, 4)} sub={`trim ${ag.trim_window?.[0] ?? '—'}-${ag.trim_window?.[1] ?? '—'}%`} icon={Activity} color="gray" />
                <Stat label="CoV%" value={ag.cov_pct == null ? '—' : `${ag.cov_pct}%`} sub="μεταβλητότητα" icon={BarChart3} color={ag.cov_pct > 50 ? 'amber' : 'emerald'} tooltip="Coefficient of variation — μεγάλη τιμή σημαίνει ότι οι σταθμοί έχουν πολύ διαφορετικά ratios" />
                <Stat label="Clamp range" value={`${ag.clamp_range?.[0] ?? '—'}–${ag.clamp_range?.[1] ?? '—'}`} sub={ag.clamped ? '🔴 clamped' : 'ok'} icon={ShieldAlert} color={ag.clamped ? 'rose' : 'gray'} />
                <Stat label="Estimated max" value={fmtNum(ls.estimated_max, 1)} sub="upper bound listeners" icon={Gauge} color="emerald" />
              </div>

              {/* Drift limit info */}
              {ls.drift_limit && (
                <div className="mb-3 text-[11px] bg-black/20 rounded p-2 border border-white/5">
                  <span className="text-gray-400">Drift limit:</span>{' '}
                  {ls.drift_limit.drift_limited ? (
                    <span className="text-amber-300">
                      🛡 limited: requested {fmtNum(ls.drift_limit.requested_ratio, 4)} → clamped to {fmtNum(ls.drift_limit.clamped_ratio, 4)}
                      <span className="text-gray-500"> (από {fmtNum(ls.drift_limit.previous_ratio, 4)}, {ls.drift_limit.age_hours}ω πριν, max {ls.drift_limit.max_change_per_hour * 100}%/h)</span>
                    </span>
                  ) : (
                    <span className="text-emerald-300">✓ χωρίς περιορισμό (εντός ορίων)</span>
                  )}
                </div>
              )}

              {/* Drift history chart */}
              {driftChart.length > 1 && (
                <div className="bg-black/20 rounded p-2 border border-white/5">
                  <div className="text-[10px] text-gray-500 mb-1">Ιστορικό ratio (48h) · {driftChart.length} δείγματα</div>
                  <ResponsiveContainer width="100%" height={140}>
                    <AreaChart data={driftChart} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#374151" strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="t" tick={{ fill: '#9ca3af', fontSize: 9 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: '#9ca3af', fontSize: 9 }} domain={['auto', 'auto']} />
                      <RTip contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 11 }} />
                      <Area type="monotone" dataKey="ratio" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.25} strokeWidth={1.5} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Anchor stations */}
              {ls.anchor_stations?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] text-gray-400 mb-1.5">Σταθμοί που τροφοδοτούν το μοντέλο ({ls.anchor_stations.length})</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className="text-gray-500 border-b border-white/5">
                        <tr>
                          <th className="text-left py-1">Σταθμός</th>
                          <th className="text-right py-1">Avg listeners</th>
                          <th className="text-right py-1">Plays/h</th>
                          <th className="text-right py-1">Ratio</th>
                          <th className="text-center py-1">Tier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ls.anchor_stations.slice(0, 10).map(a => (
                          <tr key={a.station_id} className="border-b border-white/5">
                            <td className="py-1 text-gray-200">{a.station_name}</td>
                            <td className="py-1 text-right tabular-nums">{fmtNum(a.avg_listeners, 1)}</td>
                            <td className="py-1 text-right tabular-nums">{fmtNum(a.plays_per_hour, 2)}</td>
                            <td className="py-1 text-right tabular-nums text-purple-300">{a.ratio}</td>
                            <td className="py-1 text-center"><TierBadge tier={a.tier} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Per-station table */}
            <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-white/5 text-[12px] text-gray-300 font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Per-station ({sortedStations.length}) — click row για λεπτομέρειες
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="stations-table">
                  <thead className="bg-black/30">
                    <tr>
                      <th className="w-8"></th>
                      <Th k="rank" label="#" />
                      <Th k="name" label="Σταθμός" />
                      <th className="px-2 py-2 text-left text-[11px] text-gray-400 font-semibold">Mode</th>
                      <Th k="market_share" label="Share %" align="right" />
                      <Th k="tlh_raw" label="Raw TLH" align="right" title="Πραγματικό avg × hours από real samples" />
                      <Th k="tlh_adj" label="Adj TLH" align="right" title="Τιμή που χρησιμοποιείται στο ranking" />
                      <Th k="tlh_delta" label="Δ %" align="right" title="% απόκλιση adjusted vs raw" />
                      <Th k="peak" label="Peak" align="right" />
                      <Th k="tier" label="Tier" />
                      <Th k="warnings" label="⚠" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStations.map(s => {
                      const isOpen = expanded.has(s.station_id);
                      // Estimated/D·Auto stations have intentionally zeroed
                      // TLH/share fields (backend hides "fake" audience numbers).
                      // Showing them as 0ω / 0% / -100% is misleading — render
                      // them as "—" with an explanatory tooltip instead.
                      const isEstimated = s.data_mode === 'stream' || s.scoring?.tier === 'D';
                      const deltaColor = s.tlh.delta_pct == null
                        ? 'text-gray-500'
                        : Math.abs(s.tlh.delta_pct) < 10 ? 'text-emerald-400'
                        : Math.abs(s.tlh.delta_pct) < 50 ? 'text-amber-400'
                        : 'text-rose-400';
                      const dashCell = (label) => (
                        <span
                          className="text-gray-500"
                          title={`Δεν εκτίθεται για estimated σταθμούς — δες Activity Index στις λεπτομέρειες`}
                        >—</span>
                      );
                      return (
                        <React.Fragment key={s.station_id}>
                          <tr
                            data-testid={`row-${s.station_id}`}
                            onClick={() => toggle(s.station_id)}
                            className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${s.warnings.length > 0 ? 'bg-amber-500/[0.02]' : ''}`}
                          >
                            <td className="text-center text-gray-500">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5 inline" /> : <ChevronRight className="w-3.5 h-3.5 inline" />}
                            </td>
                            <td className="px-2 py-2 text-center font-mono tabular-nums text-gray-300">#{s.rank}</td>
                            <td className="px-2 py-2 font-medium text-gray-200 truncate max-w-[220px]">{s.station_name}</td>
                            <td className="px-2 py-2"><ModeBadge mode={s.data_mode} /></td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {isEstimated ? dashCell('share') : fmtPct(s.market_share)}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-gray-400">
                              {isEstimated ? dashCell('raw') : fmtTLH(s.tlh.raw)}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-blue-300 font-semibold">
                              {isEstimated ? dashCell('adj') : fmtTLH(s.tlh.adjusted)}
                            </td>
                            <td className={`px-2 py-2 text-right tabular-nums ${deltaColor}`}>
                              {isEstimated
                                ? dashCell('delta')
                                : (s.tlh.delta_pct == null ? '—' : `${s.tlh.delta_pct > 0 ? '+' : ''}${s.tlh.delta_pct}%`)}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-emerald-300">{s.raw_listeners.peak || '—'}</td>
                            <td className="px-2 py-2"><TierBadge tier={s.scoring.tier} /></td>
                            <td className="px-2 py-2 text-center">
                              {s.warnings.length > 0 ? (
                                <span className="inline-flex items-center gap-0.5 px-1 bg-amber-500/20 text-amber-300 rounded text-[10px]">
                                  <AlertTriangle className="w-3 h-3" /> {s.warnings.length}
                                </span>
                              ) : <span className="text-emerald-400">✓</span>}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={11} className="p-0">
                                <StationDetail
                                  s={s}
                                  token={token}
                                  onRefetch={fetchData}
                                  dateFrom={period.from}
                                  dateTo={period.to}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[10px] text-gray-500 text-center pt-2 pb-6">
              Generated: {new Date(data.generated_at).toLocaleString('el-GR')} · {data.num_days} ημέρες
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RankingDiagnostics;
